import { showProviderCreditPrompt,isProviderCreditIssue } from '../../lib/providerCredits';
import { useEffect, useState, lazy, Suspense } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
const PublishDialog=lazy(()=>import('../artifacts/PublishDialog').then(m=>({default:m.PublishDialog})));
import type { Artifact } from '../../types/api';
import { Rocket } from 'lucide-react';
import { AlertTriangle, Box, Download, Film, Image as ImageIcon, Mic, Music } from 'lucide-react';
import { ModelViewer } from '../../components/ModelViewer';
import { api, download } from '../../lib/api';
import type { MediaAsset } from '../../types/api';
import { Spinner, IconButton, ErrorNote } from '../../components/ui';
import { bytes, relativeTime } from '../../lib/format';

export const mediaIcon = (k: string, size = 16) => (k === 'model3d' ? <Box size={size} /> : k === 'image' ? <ImageIcon size={size} /> : k === 'video' ? <Film size={size} /> : k === 'speech' ? <Mic size={size} /> : <Music size={size} />);
export const MEDIA_LABEL: Record<string, string> = { image: 'Image', video: 'Video', speech: 'Voiceover', music: 'Music', model3d: '3D model' };

/** Loads the file with the session (the API is cross-origin) and returns an object URL for <img>/<video>/<audio>. */
export function useMediaUrl(m: MediaAsset | undefined) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!m || m.status !== 'completed') return;
    let revoked = false, u: string | null = null;
    void api<Response>(`/api/media/${m.id}/content`, { raw: true }).then((r) => r.blob()).then((b) => { if (!revoked) { u = URL.createObjectURL(b); setUrl(u); } }).catch(() => {});
    return () => { revoked = true; if (u) URL.revokeObjectURL(u); };
  }, [m?.id, m?.status]);
  return url;
}

export function MediaPlayer({ m }: { m: MediaAsset }) {
  const url = useMediaUrl(m);
  useEffect(()=>{const issue=m.options?.providerCreditError;if(m.status==='failed'&&isProviderCreditIssue(issue))showProviderCreditPrompt(issue,`media:${m.id}`);},[m.id,m.status,m.options]);
  if (m.status === 'failed') return <div className="media__failed"><AlertTriangle size={15} /> {m.error ?? 'Generation failed'}</div>;
  if (m.status !== 'completed') return <div className="media__pending"><Spinner /> {m.kind === 'model3d' ? `Building 3D model${m.progress ? ` · ${m.progress}%` : ''}… this takes several minutes` : m.kind === 'video' ? 'Rendering video… this usually takes a few minutes' : 'Generating…'}</div>;
  if (!url) return <div className="media__pending"><Spinner /></div>;
  if (m.kind === 'model3d') return <ModelViewer src={url} alt={m.prompt} />;
  if (m.kind === 'image') return <img className="media__img" src={url} alt={m.prompt} />;
  if (m.kind === 'video') return <video className="media__video" src={url} controls playsInline />;
  return <audio className="media__audio" src={url} controls />;
}

export function MediaCard({ mediaId, media }: { mediaId: string; media?: MediaAsset }) {
  const [publishOpen,setPublishOpen]=useState(false);
  const publish=useMutation({mutationFn:()=>api<{artifact:Artifact}>(`/api/media/${mediaId}/publishing-artifact`,{body:{}}),onSuccess:()=>setPublishOpen(true)});
  const q = useQuery({
    queryKey: ['media-item', mediaId],
    queryFn: () => api<{ media: MediaAsset }>(`/api/media/${mediaId}`).then((r) => r.media),
    initialData: media,
    refetchInterval: (qq) => (qq.state.data && ['pending', 'running'].includes(qq.state.data.status) ? 5000 : false),
  });
  const m = q.data;
  if (!m) return <div className="media-card"><Spinner /></div>;
  return (<>
    <figure className="media-card" data-testid="media-card">
      <MediaPlayer m={m} />
      <figcaption>
        <span className="media-card__kind">{mediaIcon(m.kind, 13)}{MEDIA_LABEL[m.kind]}</span>
        <span className="media-card__prompt" title={m.prompt}>{m.prompt}</span>
        <span className="muted small">{m.sizeBytes ? bytes(m.sizeBytes) + ' · ' : ''}{relativeTime(m.createdAt)}</span>
        {m.status === 'completed' && <IconButton size="sm" label="Download" onClick={() => void download(`/api/media/${m.id}/content?download=true`, `lumnos-${m.kind}`)}><Download size={14} /></IconButton>}
        {m.status==='completed'&&<IconButton size="sm" label="Publish media" disabled={publish.isPending} onClick={()=>publish.mutate()}><Rocket size={14}/></IconButton>}
      </figcaption>
    </figure><ErrorNote error={publish.error}/>{publish.data&&<Suspense fallback={<Spinner/>}><PublishDialog artifact={publish.data.artifact} open={publishOpen} onOpenChange={setPublishOpen}/></Suspense>}</>
  );
}
